import { type z } from 'zod';

import { type HttpMethod } from './types';

const prependMethod = (message: string, method?: HttpMethod): string => {
  if (method) {
    return `[${method.toUpperCase()}] ${message}`;
  }
  return message;
};

const prependStatus = (message: string, status?: number): string => {
  if (status) {
    return `[${status}] ${message}`;
  }
  return message;
};

export interface HttpErrorConfig {
  readonly message?: string;
  readonly method?: HttpMethod;
  readonly url?: string;
}

export abstract class BaseHttpError extends Error {
  protected readonly url: null | string = null;
  public readonly method: HttpMethod | null = null;

  constructor({ message, method, url }: HttpErrorConfig) {
    const defaultMessage = url
      ? `There was an error making a request to ${url}.`
      : 'There was an error with the request.';
    super(prependMethod(message ?? defaultMessage, method));
    this.name = 'HttpError';
    this.url = url ?? null;
    this.method = method ?? null;
  }
}

export class HttpNetworkError extends BaseHttpError {
  public readonly error: Error;

  constructor(error: Error, config: HttpErrorConfig) {
    const defaultMessage = config.url
      ? `There was a network error making a request to ${config.url}: ${error.message}`
      : `There was a network error with the request: ${error.message}`;
    super({
      ...config,
      message: config.message ?? defaultMessage,
    });
    this.error = error;
    this.name = 'HttpNetworkError';
  }
}

export interface HttpClientErrorConfig extends HttpErrorConfig {
  readonly response: Response;
  readonly status: number;
}

export class HttpClientError extends BaseHttpError {
  public readonly response: Response;
  public readonly status: number;

  constructor({ response, status, ...config }: HttpClientErrorConfig) {
    const defaultMessage = config.url
      ? `There was a client error making a request to ${config.url}.`
      : 'There was a client error with the request.';
    super({
      ...config,
      message: prependStatus(config.message ?? defaultMessage, status),
    });
    this.status = status;
    this.name = 'HttpClientError';
    this.response = response;
  }
}

export class HttpSerializationError extends HttpClientError {
  constructor(config: HttpClientErrorConfig) {
    super({
      ...config,
      message: config.message ?? 'There was an error deserializing the response body.',
    });
    this.name = 'HttpSerializationError';
  }
}

export interface HttpParseErrorConfig extends HttpClientErrorConfig {
  readonly error: z.ZodError;
}

export class HttpParseError extends HttpSerializationError {
  public readonly error: z.ZodError;

  constructor({ error, ...config }: HttpParseErrorConfig) {
    super({
      ...config,
      message: config.message ?? 'The response body did not conform to the expected schema.',
    });
    this.name = 'HttpParseError';
    this.error = error;
  }
}

export type HttpError =
  | HttpClientError
  | HttpNetworkError
  | HttpParseError
  | HttpSerializationError;

export const isHttpError = (e: unknown): e is HttpError =>
  e instanceof HttpNetworkError || e instanceof HttpClientError;
